'use client';

import classnames from 'classnames';
import { useEffect, type CSSProperties } from 'react';
import { Icon } from '../Icon';
import { useToast, type ToastEntry, type ToastSeverity } from './ToastContext';
import styles from './Toast.module.scss';

/** How long a toast shows when it doesn't say. */
const DEFAULT_TOAST_DURATION_MS = 8000;

const SEVERITY_CLASSES: Record<ToastSeverity, string | undefined> = {
  info: styles.info,
  warning: styles.warning,
  error: styles.error,
};

/** Inline style with the fade-out's custom property, typed. */
type ToastStyle = CSSProperties & Record<'--toast-duration', string>;

interface ToastItemProps {
  entry: ToastEntry;
  onDismiss: (id: string) => void;
}

function ToastItem({ entry, onDismiss }: ToastItemProps): JSX.Element {
  const durationMs = entry.durationMs ?? DEFAULT_TOAST_DURATION_MS;
  useEffect((): (() => void) => {
    const timer = setTimeout(() => onDismiss(entry.id), durationMs);
    return () => clearTimeout(timer);
  }, [durationMs, entry.id, onDismiss]);

  // Its fade out ends as it is dismissed (Toast.module.scss).
  const style: ToastStyle = { '--toast-duration': `${durationMs}ms` };
  // Compact toasts come often during play: announced politely, never interrupting.
  const announcement = entry.compact
    ? { role: 'status', 'aria-live': 'polite' } as const
    : { role: 'alert', 'aria-live': 'assertive' } as const;
  return (
    <div className={classnames(styles.toast, SEVERITY_CLASSES[entry.severity], entry.compact && styles.compact)} style={style} {...announcement}>
      <span className={styles.message}>{entry.message}</span>
      {!entry.compact && (
        <button
          className={styles.dismiss}
          onClick={() => onDismiss(entry.id)}
          aria-label="Dismiss notification"
          type="button"
        >
          <Icon icon="decline" className={styles.dismissIcon} />
        </button>
      )}
    </div>
  );
}

/** Draws the queued toasts at the top of the screen. Render it once, inside a `ToastProvider`. */
export function Toast(): JSX.Element | null {
  const { queue, dismissToast } = useToast();

  if (queue.length === 0) {
    return null;
  }

  return (
    <div className={styles.container} aria-label="Notifications">
      {queue.map((entry) => (
        <ToastItem key={entry.id} entry={entry} onDismiss={dismissToast} />
      ))}
    </div>
  );
}

export type { ToastEntry, ToastSeverity } from './ToastContext';
